import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Card, Empty, Field, StageChip } from "../../dashboard/primitives";
import { subscribeAllUsers } from "../../projects/api";
import { api } from "../../lib/api";

const fmtWhen = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const KIND_CHIP = { broadcast: "in_progress", direct: "awaiting_approval", auto: "paid", demo: "pending" };

const NotificationsSection = () => {
  const [users, setUsers] = useState([]);
  const [audience, setAudience] = useState("all"); // 'all' | 'direct'
  const [targetUid, setTargetUid] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => subscribeAllUsers(setUsers), []);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, "notificationsLog"), orderBy("createdAt", "desc"), limit(25)),
        (snap) => setLog(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setLog([])
      ),
    []
  );

  const reachable = useMemo(() => users.filter((u) => u.fcmToken).length, [users]);
  const canSend =
    title.trim() && body.trim() && !sending && (audience === "all" || targetUid);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      if (audience === "all") {
        const { data } = await api.post("/broadcast-message", {
          title: title.trim(),
          body: body.trim(),
        });
        setResult(`Sent to ${data.recipients} user${data.recipients === 1 ? "" : "s"}`);
      } else {
        await api.post("/send-message", {
          userId: targetUid,
          title: title.trim(),
          body: body.trim(),
          data: { type: "direct" },
        });
        const target = users.find((u) => (u.uid || u.id) === targetUid);
        setResult(`Sent to ${target?.displayName || target?.email || "user"}`);
      }
      setTitle("");
      setBody("");
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fw-stack-md" style={{ display: "grid", gridTemplateColumns: "1fr 1.2fr", gap: 20, alignItems: "start" }}>
      <Card
        title="Send a notification"
        action={
          <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
            {reachable}/{users.length} devices reachable
          </span>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Audience */}
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { id: "all", label: "All users" },
              { id: "direct", label: "One customer" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className="fw-btn"
                onClick={() => setAudience(opt.id)}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  fontSize: 12,
                  background: audience === opt.id ? "var(--fw-ink)" : "transparent",
                  color: audience === opt.id ? "var(--fw-cream)" : "var(--fw-ink)",
                  border: "1.5px solid var(--fw-ink)",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {audience === "direct" && (
            <Field label="Customer">
              <select
                className="fw-input"
                value={targetUid}
                onChange={(e) => setTargetUid(e.target.value)}
              >
                <option value="">Select a customer…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.uid || u.id}>
                    {u.displayName || u.email} {u.fcmToken ? "" : "(no device)"}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Title">
            <input
              className="fw-input"
              value={title}
              maxLength={60}
              placeholder="New case study is live"
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>

          <Field label="Message">
            <textarea
              className="fw-input"
              rows={3}
              value={body}
              maxLength={180}
              placeholder="Take a look at what we shipped this week…"
              onChange={(e) => setBody(e.target.value)}
              style={{ resize: "vertical" }}
            />
          </Field>

          {/* Preview */}
          {(title || body) && (
            <div
              style={{
                background: "var(--fw-ink)",
                borderRadius: 12,
                padding: "12px 14px",
                color: "var(--fw-cream)",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700 }}>{title || "Title"}</div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>{body || "Message body"}</div>
            </div>
          )}

          {error && (
            <div style={{ fontSize: 12, color: "var(--fw-red)", fontFamily: "var(--fw-mono)" }}>{error}</div>
          )}
          {result && (
            <div style={{ fontSize: 12, color: "var(--fw-muted)", fontFamily: "var(--fw-mono)" }}>✓ {result}</div>
          )}

          <button
            className="fw-btn fw-btn--primary"
            style={{ padding: "12px 16px", fontSize: 12 }}
            disabled={!canSend}
            onClick={send}
          >
            {sending
              ? "Sending…"
              : audience === "all"
              ? "Broadcast to everyone"
              : "Send to customer"}
          </button>
        </div>
      </Card>

      <Card title="Recent sends">
        {log.length === 0 ? (
          <Empty>Nothing sent yet</Empty>
        ) : (
          log.map((n) => (
            <div
              key={n.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 0",
                borderBottom: "1px solid var(--fw-line)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{n.title}</div>
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>{n.body}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <StageChip status={KIND_CHIP[n.kind] || "pending"} />
                <div style={{ fontSize: 11, color: "var(--fw-muted)", marginTop: 4, fontFamily: "var(--fw-mono)" }}>
                  {n.kind} · {n.recipients} recv · {fmtWhen(n.createdAt)}
                </div>
              </div>
            </div>
          ))
        )}
      </Card>
    </div>
  );
};

export default NotificationsSection;
